import React, { useState, useMemo, useEffect } from "react";

const VOUCHER_TYPE_BADGES = {
  1: { name: "Receipt", cls: "vch_badge_receipt" },
  2: { name: "Payment", cls: "vch_badge_payment" },
  3: { name: "Journal", cls: "vch_badge_journal" },
  4: { name: "Contra", cls: "vch_badge_contra" },
  5: { name: "Sales", cls: "vch_badge_sales" },
  6: { name: "Purchase", cls: "vch_badge_purchase" },
  7: { name: "Debit Note", cls: "vch_badge_debit_note" },
  8: { name: "Credit Note", cls: "vch_badge_credit_note" },
};

export default function VoucherSearchModal({
  isOpen,
  onClose,
  vouchers = [],
  onSelectVoucher,
  voucherTypes = [],
  currentLoadedVoucherId = null,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTypeFilter, setSelectedTypeFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    if (isOpen) {
      setSearchTerm("");
      setSelectedTypeFilter("all");
      setFromDate("");
      setToDate("");
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const formatINR = (val) => {
    const num = Number(val || 0);
    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const day = String(d.getDate()).padStart(2, "0");
      const monthNames = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
      ];
      return `${day}-${monthNames[d.getMonth()]}-${d.getFullYear()}`;
    } catch {
      return dateStr;
    }
  };

  const getBadgeClass = (vtype) => {
    const item = VOUCHER_TYPE_BADGES[vtype];
    return item ? item.cls : "vch_badge_general";
  };

  const filteredVouchers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return vouchers.filter((v) => {
      // 1. Voucher Type Filter
      if (selectedTypeFilter !== "all") {
        const typeNum = parseInt(selectedTypeFilter, 10);
        if (v.vtype !== typeNum && String(v.vtype) !== selectedTypeFilter) {
          return false;
        }
      }

      // 2. Date Filter
      if (fromDate || toDate) {
        if (!v.voucherDate) return false;
        const vDate = v.voucherDate.slice(0, 10);
        if (fromDate && vDate < fromDate) return false;
        if (toDate && vDate > toDate) return false;
      }

      // 3. Search Term
      if (!term) return true;

      const vNo = String(v.voucherNo || "").toLowerCase();
      const refNo = String(v.referenceNo || "").toLowerCase();
      const party = String(v.partyName || "").toLowerCase();
      const nar = String(v.narration || "").toLowerCase();
      const typeName = String(v.vtypeName || "").toLowerCase();
      const amt = String(v.amount || "");

      return (
        vNo.includes(term) ||
        refNo.includes(term) ||
        party.includes(term) ||
        nar.includes(term) ||
        typeName.includes(term) ||
        amt.includes(term)
      );
    });
  }, [vouchers, searchTerm, selectedTypeFilter, fromDate, toDate]);

  if (!isOpen) return null;

  return (
    <div className="vch_modal_backdrop" onClick={onClose}>
      <div
        className="vch_modal_dialog vch_modal_search_dialog"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="vch_modal_header vch_search_modal_header">
          <div className="vch_modal_title_area">
            <i className="fa-solid fa-file-invoice"></i>
            <h5>Search & Select Voucher to Modify</h5>
            <span className="vch_search_count_badge">
              {filteredVouchers.length} of {vouchers.length} vouchers
            </span>
          </div>
          <button
            type="button"
            className="vch_modal_close_btn"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        {/* Filter Controls Bar */}
        <div className="vch_search_modal_filters">
          <div className="vch_search_modal_input_wrap">
            <i className="fa-solid fa-magnifying-glass vch_search_icon"></i>
            <input
              type="text"
              className="vch_search_input_box"
              placeholder="Search by Voucher No, Party Name, Reference No, Narration, Amount..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
            {searchTerm && (
              <button
                type="button"
                className="vch_search_clear_btn"
                onClick={() => setSearchTerm("")}
                title="Clear search"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            )}
          </div>

          {/* Date range filters */}
          <div className="vch_search_date_range">
            <div className="vch_date_field">
              <label>From:</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="vch_date_field">
              <label>To:</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            {(fromDate || toDate) && (
              <button
                type="button"
                className="vch_btn_clear_date"
                onClick={() => {
                  setFromDate("");
                  setToDate("");
                }}
                title="Reset date filters"
              >
                Clear Dates
              </button>
            )}
          </div>
        </div>

        {/* Type Filter Pills */}
        <div className="vch_type_pills_container">
          <span className="vch_pills_label">Type:</span>
          <button
            type="button"
            className={`vch_type_pill ${selectedTypeFilter === "all" ? "active" : ""}`}
            onClick={() => setSelectedTypeFilter("all")}
          >
            All Types
          </button>
          {voucherTypes.map((vt) => (
            <button
              key={vt.id || vt.vtype}
              type="button"
              className={`vch_type_pill ${
                String(selectedTypeFilter) === String(vt.vtype) ? "active" : ""
              }`}
              onClick={() => setSelectedTypeFilter(String(vt.vtype))}
            >
              {vt.name || vt.vtypeName}
            </button>
          ))}
        </div>

        {/* Results Table */}
        <div className="vch_search_results_container">
          <table className="vch_search_results_table">
            <thead>
              <tr>
                <th style={{ width: "85px" }}>Voucher No</th>
                <th style={{ width: "95px" }}>Date</th>
                <th style={{ width: "100px" }}>Type</th>
                <th>Party / Particulars</th>
                <th style={{ width: "110px" }}>Ref No</th>
                <th style={{ width: "180px" }}>Narration</th>
                <th style={{ width: "115px", textAlign: "right" }}>Amount (₹)</th>
                <th style={{ width: "95px", textAlign: "center" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredVouchers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="vch_search_empty_cell">
                    <i className="fa-regular fa-folder-open"></i>
                    <p>No vouchers match your search criteria</p>
                    <button
                      type="button"
                      className="vch_btn_reset_search"
                      onClick={() => {
                        setSearchTerm("");
                        setSelectedTypeFilter("all");
                        setFromDate("");
                        setToDate("");
                      }}
                    >
                      Reset All Filters
                    </button>
                  </td>
                </tr>
              ) : (
                filteredVouchers.map((v) => {
                  const isCurrent =
                    String(v.voucherId) === String(currentLoadedVoucherId);

                  return (
                    <tr
                      key={v.voucherId}
                      className={`vch_search_row ${isCurrent ? "vch_search_row_active" : ""}`}
                      onDoubleClick={() => {
                        onSelectVoucher(v.voucherId);
                        onClose();
                      }}
                      title="Double click to select and load into modification form"
                    >
                      <td className="vch_col_voucher_no">
                        <strong>#{v.voucherNo}</strong>
                      </td>
                      <td className="vch_col_date">{formatDate(v.voucherDate)}</td>
                      <td>
                        <span className={`vch_type_badge ${getBadgeClass(v.vtype)}`}>
                          {v.vtypeName || v.vtype}
                        </span>
                      </td>
                      <td className="vch_col_party" title={v.partyName || "-"}>
                        {v.partyName && v.partyName !== "N/A" ? (
                          <strong>{v.partyName}</strong>
                        ) : (
                          <span style={{ color: "#94a3b8" }}>—</span>
                        )}
                      </td>
                      <td className="vch_col_ref">{v.referenceNo || "—"}</td>
                      <td className="vch_col_narration" title={v.narration || ""}>
                        {v.narration || "—"}
                      </td>
                      <td className="vch_col_amount">
                        ₹{formatINR(v.amount)}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          type="button"
                          className={`vch_btn_select_modify ${isCurrent ? "btn_selected" : ""}`}
                          onClick={() => {
                            onSelectVoucher(v.voucherId);
                            onClose();
                          }}
                          title="Load this voucher for modification"
                        >
                          {isCurrent ? (
                            <>
                              <i className="fa-solid fa-check"></i> Loaded
                            </>
                          ) : (
                            <>
                              <i className="fa-solid fa-pen"></i> Select
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="vch_modal_footer vch_search_modal_footer">
          <span className="vch_search_hint">
            <i className="fa-regular fa-lightbulb"></i> Tip: Double-click any row to instantly load it into the form.
          </span>
          <button type="button" className="vch_btn_secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
